import { CloseIcon } from 'assets/icons/CloseIcon'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { CheckBox } from 'components/CheckBox/CheckBox'
import { Collapsable } from 'components/Collapsable/Collapsable'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { Input } from 'components/Input/Input'
import { Modal } from 'components/Modal/Modal'
import { ISelectValue } from 'constants/globalTypes'
import { clearFoodReport, confirmFoodReport, editFoodReport, generateFoodReport, getFoodReport } from 'features/food/foodSlice'
import useModalClose from 'hooks/useModalClose'
import { dietaryPreferences } from 'pages/Food/foodData'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { AppDispatch, RootState } from 'store'
import { Button, CursorPointer, Flex, Separator } from 'styling/GlobalStyles'
import { BodyText, TitleText } from 'styling/Typography'
import { IFoodReport } from 'types/api/foodApi.interface'

export const FoodReportModal = ({ closeModal }: { closeModal: any }) => {
    const [date, setDate] = useState(new Date())
    const { foodReport, oldFoodReport } = useSelector((state: RootState) => state.food)
    const [localReport, setLocalReport] = useState<IFoodReport | null>(null)
    const [toggleGuestMeals, setToggleGuestMeals] = useState(false)
    const [guestMeals, setGuestMeals] = useState([])
    const [selectedMealType, setSelectedMealType] = useState<ISelectValue | null>(null)
    const [addingMeal, setAddingMeal] = useState({
        dietaryPreferenceType: null,
        supplier: null,
        mealTypes: [],
        count: 0
    })
    const dispatch = useDispatch<AppDispatch>()
    useModalClose(closeModal)

    useEffect(() => {
        if (oldFoodReport) {
            setLocalReport(oldFoodReport)
        }
    }, [oldFoodReport])

    const handleGenerate = async () => {
        if (oldFoodReport && localReport) {
            const payload: any = {
                id: localReport.id,
                meals: (localReport.meals || []).map(m => ({ id: m.id, count: m.count })),
                guestMeals: [
                    ...(localReport.guestMeals || []).map((g: any) => ({
                        id: g.id,
                        mealSupplierId: g.supplier?.id,
                        mealTypeIds: g.mealType ? [g.mealType.id] : [],
                        // dietaryPreferenceType may not exist for existing items; backend should keep existing value,
                        dietaryPreferenceType: g.dietaryPreferenceType || null,
                        count: g.count
                    })),
                    ...guestMeals.map((m: any) => ({
                        mealSupplierId: m.supplier?.value,
                        mealTypeIds: (m.mealTypes || []).map((mt: any) => mt.value),
                        dietaryPreferenceType: m.dietaryPreferenceType?.value,
                        count: m.count,
                    }))
                ]
            }
            const resp = await dispatch(editFoodReport({ report: payload, id: localReport.id }))
            if ((resp as any)?.meta?.requestStatus === 'fulfilled') {
                closeModal()
                dispatch(clearFoodReport())
            }
            return
        }

        if (!foodReport?.meals?.length) {
            await dispatch(generateFoodReport({
                date: date.toISOString(), guestMeals: guestMeals.map(m => ({
                    count: m.count,
                    dietaryPreferenceType: m.dietaryPreferenceType.value,
                    supplierId: m.supplier.value,
                    mealTypeIds: m.mealTypes.map(mt => mt.value)
                }))
            }))

        } else {
            const resp = await dispatch(confirmFoodReport(foodReport.id))
            if (resp.meta.requestStatus === 'fulfilled') {
                closeModal()
                dispatch(clearFoodReport())
            }
        }

    }

    useEffect(() => {
        dispatch(getFoodReport(date))
    }, [date])

    function formatFoodReport(data: IFoodReport): TransformedFoodReport[] {
        const shiftMap = new Map<number, TransformedFoodReport & { _suppliersMap: Map<number, Supplier & { _mealsMap: Map<number, Meal & { _prefMap: Map<number, number> }> }> }>();

        const formatTime = (minutes: number): string => {
            const h = Math.floor(minutes / 60).toString().padStart(2, '0');
            const m = (minutes % 60).toString().padStart(2, '0');
            return `${h}:${m}`;
        }

        for (const meal of data.meals) {
            const {
                shift,
                supplier,
                dish,
                dietaryPreferenceType,
                count
            } = meal;

            if (!shiftMap.has(shift.id)) {
                shiftMap.set(shift.id, {
                    shiftName: shift.name,
                    timeRange: `${formatTime(shift.shiftStart)} - ${formatTime(shift.shiftStart + shift.minutes)}`,
                    suppliers: [],
                    _suppliersMap: new Map()
                });
            }

            const shiftEntry = shiftMap.get(shift.id);
            if (!shiftEntry) continue

            if (!shiftEntry._suppliersMap.has(supplier.id)) {
                shiftEntry._suppliersMap.set(supplier.id, {
                    name: supplier.name,
                    meals: [],
                    _mealsMap: new Map()
                });
            }

            const supplierEntry = shiftEntry._suppliersMap.get(supplier.id);
            if (!supplierEntry) continue

            if (!supplierEntry._mealsMap.has(dish.id)) {
                supplierEntry._mealsMap.set(dish.id, {
                    mealName: dish.name,
                    dietaryPreferencesArray: [],
                    _prefMap: new Map()
                });
            }

            const mealEntry = supplierEntry._mealsMap.get(dish.id);
            if (!mealEntry) continue

            const currentCount = mealEntry._prefMap.get(dietaryPreferenceType) || 0;
            mealEntry._prefMap.set(dietaryPreferenceType, currentCount + count);
        }

        const result: TransformedFoodReport[] = [];

        for (const shiftEntry of shiftMap.values()) {
            const suppliers: Supplier[] = [];

            for (const supplier of shiftEntry._suppliersMap.values()) {
                const meals: Meal[] = [];

                for (const meal of supplier._mealsMap.values()) {
                    const dietaryPreferencesArray: DietaryPreference[] = [];

                    for (const [prefType, cnt] of meal._prefMap.entries()) {
                        dietaryPreferencesArray.push({
                            name: `Preference ${dietaryPreferences.find(p => p.value == prefType).label}`, // You may map preference types to real names
                            count: cnt
                        });
                    }

                    meals.push({
                        mealName: meal.mealName,
                        dietaryPreferencesArray
                    });
                }

                suppliers.push({
                    name: supplier.name,
                    meals
                });
            }

            result.push({
                shiftName: shiftEntry.shiftName,
                timeRange: shiftEntry.timeRange,
                suppliers
            });
        }

        return result;
    }

    const handleDate = d => {
        setDate(d)
        dispatch(clearFoodReport())
        setGuestMeals([])
        setSelectedMealType(null)
        setAddingMeal({
            dietaryPreferenceType: null,
            supplier: null,
            mealTypes: [],
            count: 0
        })
    }


    const handleClose = () => {
        closeModal()
        dispatch(clearFoodReport())
    }


    const parse = (data: any) => {
        return data.data.map((item) => ({ label: item.name, value: item.id }))
    }

    const handleSelect = (value: ISelectValue, name: string) => {
        setAddingMeal(prev => {
            const copy = { ...prev }
            copy[name] = value
            return copy
        })
    }

    const addGuestMeal = () => {
        if (!addingMeal.dietaryPreferenceType || !addingMeal.supplier || !addingMeal.mealTypes.length || !addingMeal.count) {
            toast.error('Please fill all fields before adding a guest meal')
            return
        }
        setGuestMeals(prev => [...prev, addingMeal])
        setAddingMeal({
            dietaryPreferenceType: null,
            supplier: null,
            mealTypes: [],
            count: 0
        })
    }

    const addMealType = () => {
        if (!selectedMealType) {
            toast.error('Please select a meal type to add')
            return
        }
        setAddingMeal(prev => ({ ...prev, mealTypes: [...prev.mealTypes, selectedMealType] }))
        setSelectedMealType(null)
    }

    const handleToggle = () => {
        setToggleGuestMeals(p => !p)
        setGuestMeals([])
    }

    return <Modal black >
        <Flex column between center style={{ minHeight: '60vh', maxHeight: '90vh', overflow: 'auto' }} >
            <Flex between width='100%' center style={{ marginBottom: '1rem' }}>
                <TitleText weight='bold' >Food Report</TitleText>
                <CursorPointer onClick={handleClose} >
                    <CloseIcon />
                </CursorPointer>
            </Flex>

            <Flex column gap='1rem' style={{ flex: 1 }} width='100%' >

                <CalendarInput labelText='Date' defaultValue={date} onChange={handleDate} />
                {foodReport || localReport ? (foodReport?.meals?.length || localReport?.meals.length ?
                    <Flex column style={{ maxHeight: '50vh', overflow: 'auto' }} >
                        {!oldFoodReport && (formatFoodReport(foodReport || localReport).map(shift => (
                            <Flex key={shift.shiftName} column gap="0.5rem" >
                                <TitleText weight="bold">{shift.shiftName} ({shift.timeRange})</TitleText>
                                {shift.suppliers.map(supplier => (
                                    <Flex key={supplier.name} column gap="0.25rem" style={{ paddingLeft: '1rem' }}>
                                        <BodyText weight="bold">{supplier.name}</BodyText>
                                        {supplier.meals.map(meal => (
                                            <Flex key={meal.mealName} column gap="0.25rem" style={{ paddingLeft: '1rem' }}>
                                                <BodyText weight="bold">{meal.mealName}</BodyText>
                                                {meal.dietaryPreferencesArray.map(pref => (
                                                    <BodyText key={pref.name} style={{ paddingLeft: '1rem' }}>
                                                        {pref.name}: {pref.count}
                                                    </BodyText>
                                                ))}
                                            </Flex>
                                        ))}
                                    </Flex>
                                ))}
                                <Separator />
                            </Flex>
                        )))}

                        {oldFoodReport && localReport && (
                            <Flex column gap='0.75rem'>
                                <TitleText weight='bold'>Edit Meals</TitleText>
                                {(() => {
                                    const byShift: Record<number, { name: string; items: { meal: any; idx: number }[] }> = {}
                                        ; (localReport.meals || []).forEach((m: any, idx: number) => {
                                            const sid = m.shift?.id
                                            if (sid == null) return
                                            if (!byShift[sid]) byShift[sid] = { name: m.shift?.name || 'Shift', items: [] }
                                            byShift[sid].items.push({ meal: m, idx })
                                        })
                                    return Object.values(byShift).map(section => (
                                        <Flex key={section.name} column gap='0.5rem'>
                                            <TitleText weight='bold'>{section.name}</TitleText>
                                            {section.items.map(({ meal, idx }) => (
                                                <Flex key={meal.id} between center gap='0.75rem' style={{ paddingLeft: '1rem' }}>
                                                    <BodyText weight='bold'>{meal.supplier?.name || ''} • {meal.dish?.name || ''}</BodyText>
                                                    <Flex alignEnd gap='0.5rem'>
                                                        <div style={{ width: '96px' }}>
                                                            <Input
                                                                labelText='Count'
                                                                type='number'
                                                                value={meal.count}
                                                                onChange={(e) => {
                                                                    const value = Number(e.target.value)
                                                                    setLocalReport(prev => {
                                                                        const copy: any = { ...prev }
                                                                        copy.meals = [...copy.meals]
                                                                        copy.meals[idx] = { ...copy.meals[idx], count: value }
                                                                        return copy
                                                                    })
                                                                }}
                                                                placeholder='0'
                                                                nomarg
                                                            />
                                                        </div>
                                                        <Button
                                                            onClick={() => {
                                                                setLocalReport(prev => {
                                                                    const copy: any = { ...prev }
                                                                    copy.meals = [...copy.meals]
                                                                    copy.meals[idx] = { ...copy.meals[idx], count: 0 }
                                                                    return copy
                                                                })
                                                            }}
                                                            style={{ background: '#2c2c2c' }}
                                                        >Remove</Button>
                                                    </Flex>
                                                </Flex>
                                            ))}
                                            <Separator />
                                        </Flex>
                                    ))
                                })()}

                                {(localReport.guestMeals?.length > 0 || toggleGuestMeals) && (
                                    <>
                                        <Separator />
                                        <TitleText weight='bold'>Edit Guest Meals</TitleText>
                                        {(localReport.guestMeals || []).map((g: any, gIdx: number) => (
                                            <Flex key={g.id} between center gap='0.75rem'>
                                                <BodyText>{g.supplier?.name || ''} • {g.mealType?.name || ''} • {g.dish?.name || ''}</BodyText>
                                                <Input
                                                    labelText='Count'
                                                    type='number'
                                                    value={g.count}
                                                    onChange={(e) => {
                                                        const value = Number(e.target.value)
                                                        setLocalReport(prev => {
                                                            const copy: any = { ...prev }
                                                            copy.guestMeals = [...(copy.guestMeals || [])]
                                                            copy.guestMeals[gIdx] = { ...copy.guestMeals[gIdx], count: value }
                                                            return copy
                                                        })
                                                    }}
                                                    placeholder='0'
                                                    nomarg
                                                />
                                            </Flex>
                                        ))}
                                    </>
                                )}
                            </Flex>
                        )}

                        {guestMeals.length > 0 && <Flex width='100%' column gap='0.5rem' >
                            <TitleText weight='bold'>Guest Meals</TitleText>
                            {guestMeals.map((m, i) => <Flex key={i} >
                                <BodyText>{m.count}x {dietaryPreferences.find(dp => dp.value === m.dietaryPreferenceType?.value)?.label} meals from {m.supplier?.label} for: {m.mealTypes.map(mt => mt.label).join(', ')}</BodyText>
                            </Flex>)}
                        </Flex>}
                    </Flex>
                    : <BodyText>No Reports available for selected date</BodyText>) : null}
                {(toggleGuestMeals) &&
                    <Collapsable defaultOpen={true} text='Add Guest Meal' >
                        <Flex column gap='1rem' width='100%' >
                            <Separator />

                            <Flex end='true' alignEnd gap='1rem' >
                                <HonestSelectInput
                                    options={dietaryPreferences}
                                    name='dietaryPreferenceType'
                                    defaultValue={addingMeal.dietaryPreferenceType?.value}
                                    labelText='Dietary Preference'
                                    onChange={handleSelect}
                                    experimentalResetValue={true}
                                    nomarg />
                                <HonestSelectAsyncInput
                                    name='supplier'
                                    labelText='Meal Supplier'
                                    onChange={handleSelect}
                                    placeholder='Main Kitchen..'
                                    link='food/management/meal/supplier'
                                    parserFunction={parse}
                                    defaultValue={addingMeal.supplier?.label}
                                    nomarg
                                />
                                <Input
                                    labelText='Quantity'
                                    type='number'
                                    value={addingMeal.count}
                                    onChange={(e) => setAddingMeal(prev => ({ ...prev, count: Number(e.target.value) }))}
                                    placeholder='5'
                                    nomarg
                                />
                            </Flex>
                            <Flex column between alignEnd gap='1rem' width='100%'  >
                                <Flex width='100%' between alignEnd >
                                    <Flex gap='1rem' alignEnd width='60%' >
                                        <HonestSelectAsyncInput
                                            name='mealTypes'
                                            labelText='Meal Types'
                                            onChange={v => setSelectedMealType(v)}
                                            placeholder='Select Meal Type'
                                            defaultValue={selectedMealType?.label}
                                            link='food/management/meal/type'
                                            parserFunction={parse}
                                            nomarg
                                        />
                                        <Button disabled={!selectedMealType?.value} orange onClick={addMealType} >Add Type</Button>
                                    </Flex>
                                    <Button disabled={!addingMeal.dietaryPreferenceType?.value || !addingMeal.supplier?.value || !addingMeal.mealTypes.length || !addingMeal.count} orange onClick={addGuestMeal} >Add Guest Meal</Button>
                                </Flex>
                                {addingMeal?.mealTypes.length > 0 && <Flex column width='100%' >
                                    <TitleText>Meal Types:</TitleText>
                                    <BodyText>{addingMeal.mealTypes.map(mt => mt.label).join(', ')}</BodyText>
                                </Flex>}
                            </Flex>

                        </Flex>
                    </Collapsable>}
                {(foodReport?.meals?.length || oldFoodReport) ? (
                    <Flex mb='16' width='100%'>
                        <CheckBox state={toggleGuestMeals} onClick={handleToggle} square title={oldFoodReport ? 'Add guest meals' : 'Include guest meals'} />
                    </Flex>
                ) : null}
            </Flex>
            <Button disabled={!date} orange onClick={handleGenerate} >{oldFoodReport ? 'Edit' : foodReport?.meals?.length ? 'Confirm' : 'Generate'}</Button>
        </Flex>
    </Modal>
}



interface DietaryPreference {
    name: string
    count: number
}

interface Meal {
    mealName: string
    dietaryPreferencesArray: DietaryPreference[]
}

interface Supplier {
    name: string
    meals: Meal[]
}

interface TransformedFoodReport {
    shiftName: string
    timeRange: string // npr. "00:00 - 09:00"
    suppliers: Supplier[]
}